import React, { useEffect, useState } from 'react';
import { Sparkles, Wrench, X } from 'lucide-react';
import { CURRENT_RELEASE, RELEASE_NOTES } from '../../releaseNotes';

// Shows the current release's notes once per browser (after the user refreshes to the new build).
// Closes on the button or click-outside.
export default function ReleaseNotesModal() {
  const [show, setShow] = useState(false);
  const note = RELEASE_NOTES[CURRENT_RELEASE];

  useEffect(() => {
    if (!localStorage.getItem('oms_token')) return; // only for logged-in users
    // `silent: true` = an internal/security release: recorded here (and in the GitHub release) but
    // never announced to sellers in the popup.
    if (!note || note.silent) return;
    if (localStorage.getItem('oms_seen_release') !== CURRENT_RELEASE) {
      setShow(true);
      // Mark as seen the moment it's shown — not only on close — so it can never re-appear even if
      // the user dismisses it by navigating / a redirect re-render before clicking "Got it".
      try { localStorage.setItem('oms_seen_release', CURRENT_RELEASE); } catch (_) {}
    }
  }, [note]);

  const close = () => { try { localStorage.setItem('oms_seen_release', CURRENT_RELEASE); } catch (_) {} setShow(false); };

  if (!show || !note) return null;

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <button style={s.close} onClick={close}><X size={18} /></button>
        <div style={s.head}>
          <div style={s.badge}>What's New</div>
          <div style={s.ver}>{CURRENT_RELEASE}<span style={s.date}> · {note.date}</span></div>
        </div>

        {note.features?.length > 0 && (
          <div style={s.section}>
            <div style={s.featHead}><Sparkles size={15} /> New features</div>
            {note.features.map((f, i) => (
              <div key={i} style={s.featRow}>
                <span style={s.newTag}>NEW</span>
                <span style={s.featText}>{f}</span>
              </div>
            ))}
          </div>
        )}

        {note.fixes?.length > 0 && (
          <div style={s.section}>
            <div style={s.fixHead}><Wrench size={13} /> Fixes &amp; improvements</div>
            {note.fixes.map((f, i) => <div key={i} style={s.fixRow}>• {f}</div>)}
          </div>
        )}

        <button style={s.gotIt} onClick={close}>Got it</button>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4500, padding: 16 },
  modal: { position: 'relative', background: '#fff', borderRadius: 16, padding: '24px 24px 20px', width: 460, maxWidth: '94vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: 'var(--mc-e4)' },
  close: { position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  head: { marginBottom: 18 },
  badge: { display: 'inline-block', background: 'linear-gradient(90deg,#6366f1,#8b5cf6)', color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: 0.5, padding: '4px 12px', borderRadius: 20 },
  ver: { marginTop: 8, fontSize: 18, fontWeight: 800, color: '#0f172a' },
  date: { color: '#94a3b8', fontSize: 13, fontWeight: 500 },
  section: { marginTop: 16 },
  featHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 },
  featRow: { display: 'flex', gap: 10, alignItems: 'flex-start', background: '#f5f3ff', border: '1px solid #ede9fe', borderRadius: 10, padding: '10px 12px', marginBottom: 8 },
  newTag: { flexShrink: 0, background: '#22c55e', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 5, marginTop: 1 },
  featText: { color: '#1e293b', fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 },
  fixHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 7 },
  fixRow: { color: '#64748b', fontSize: 12.5, lineHeight: 1.6, paddingLeft: 2 },
  gotIt: { marginTop: 20, width: '100%', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};
